/* eslint-disable indent */
import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import { Controller, ControllerProps, FieldPath, FieldValues, FormProvider, useFormContext } from 'react-hook-form';

import { cn } from '@/slw/shared/lib/utils';
import { Label } from '@/slw/shared/ui/label';

const Form = FormProvider;

const isDebugErrorsEnabled = () => {
  if (typeof window === 'undefined') return false;

  try {
    const url = new URL(window.location.href);
    const queryDebug = url.searchParams.get('debug-errors');

    if (queryDebug === '1' || queryDebug === 'true') return true;

    return window.localStorage.getItem('debug-errors') === '1';
  } catch {
    return false;
  }
};

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

const FormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const id = React.useId();

    return (
      <FormItemContext.Provider value={{ id }}>
        <div ref={ref} className={cn('space-y-2', className)} {...props} />
      </FormItemContext.Provider>
    );
  },
);

FormItem.displayName = 'FormItem';

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();

  return <Label ref={ref} className={cn(error && 'text-destructive', className)} htmlFor={formItemId} {...props} />;
});

FormLabel.displayName = 'FormLabel';

const FormControl = React.forwardRef<React.ElementRef<typeof Slot>, React.ComponentPropsWithoutRef<typeof Slot>>(
  ({ children, ...props }, ref) => {
    const { error, formItemId, formDescriptionId, formMessageId, name } = useFormField();

    // Debug: Check if children is valid BEFORE rendering Slot
    const childrenCount = React.Children.count(children);
    if (childrenCount !== 1) {
      const debugPayload = {
        fieldName: name,
        formItemId,
        childrenCount,
        children: React.Children.map(children, (child) => ({
          type: typeof child,
          displayName: (child as any)?.type?.displayName || (child as any)?.type?.name || 'Unknown',
          props: (child as any)?.props ? Object.keys((child as any).props) : [],
        })),
        stackTrace: new Error().stack,
      };

      if (typeof window !== 'undefined') {
        console.error('[FormControl Debug] Multiple children detected:', debugPayload);
      }

      if (isDebugErrorsEnabled()) {
        const error = new Error(
          `[FormControl] Expected a single child. fieldName=${name ?? 'unknown'} formItemId=${formItemId} childrenCount=${childrenCount}`,
        );

        (error as any).detail = debugPayload;
        throw error;
      }
    }

    return (
      <Slot
        ref={ref}
        id={formItemId}
        aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
        aria-invalid={!!error}
        {...props}
      >
        {children}
      </Slot>
    );
  },
);

FormControl.displayName = 'FormControl';

const FormDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => {
    const { formDescriptionId } = useFormField();

    return <p ref={ref} id={formDescriptionId} className={cn('text-sm text-muted-foreground', className)} {...props} />;
  },
);

FormDescription.displayName = 'FormDescription';

interface FormMessageProps extends React.HTMLAttributes<HTMLParagraphElement> {
  tooltip?: boolean;
}
const FormMessage = React.forwardRef<HTMLParagraphElement, FormMessageProps>(
  ({ className, children, tooltip = false, ...props }, ref) => {
    const { error, formMessageId } = useFormField();
    const body = error ? String(error?.message) : children;

    if (!body) {
      return null;
    }

    return (
      <p
        ref={ref}
        id={formMessageId}
        className={cn('text-xs text-destructive leading-none px-1.5 py-2  rounded-0.5', className, {
          ' bg-destructive text-destructive-foreground  inline-block': tooltip,
        })}
        {...props}
      >
        {body}
      </p>
    );
  },
);

FormMessage.displayName = 'FormMessage';

export { useFormField, Form, FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormField };
